import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_FORM_BINDING_URL,
  GET_FORMS_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  REMOVE_FORM_BINDING_URL,
  get_request_config
} from '@/utils/system_constants/constant';
import {
  get_response_items,
  parse_response_payload,
  unique_positive_ids
} from './bind_documents_helpers';
import bind_document_methods from './bind_documents_methods';

export default {
  name: 'BindDocuments',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    entityTypeName: {
      type: String,
      required: false,
      default: '',
    },
    entityConfigId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    loading: false,
    forms_loading: false,
    bindings_loading: false,
    creating_form_binding: false,
    removing_binding_id: null,
    form_dropdown_open: false,
    selected_form_ids: [],
    available_forms: [],
    document_form_bindings: [],
  }),
  watch: {
    entityTypeId() {
      this.load_document_form_workspace();
    },
    entityConfigId() {
      this.load_document_form_workspace();
    }
  },
  computed: {
    available_forms_by_id() {
      return new Map(
        (this.available_forms || [])
          .filter((form) => Number(form?.id || 0) > 0)
          .map((form) => [Number(form.id), form])
      );
    },
    bound_form_ids() {
      return new Set(
        (this.document_form_bindings || [])
          .map((binding) => Number(binding?.form || binding?.form_id || 0))
          .filter((id) => Number.isFinite(id) && id > 0)
      );
    },
    bindable_forms() {
      return (this.available_forms || []).filter(
        (form) => !this.bound_form_ids.has(Number(form?.id || 0))
      );
    },
    bound_form_count() {
      return Array.isArray(this.document_form_bindings) ? this.document_form_bindings.length : 0;
    },
    bindable_form_count() {
      return Array.isArray(this.bindable_forms) ? this.bindable_forms.length : 0;
    },
    selected_form_count() {
      return Array.isArray(this.selected_form_ids) ? this.selected_form_ids.length : 0;
    },
    selected_form_label() {
      const states = [
        [this.forms_loading, 'Loading document forms...'],
        [!this.bindable_form_count, 'No document forms available'],
        [!this.selected_form_count, 'Select document form(s) to bind']
      ];
      const matched_state = states.find(([is_active]) => is_active);
      return matched_state?.[1] || this.selected_forms_summary_label();
    },
  },
  mounted() {
    this.load_document_form_workspace();
    document.addEventListener('click', this.on_document_click, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.on_document_click, true);
  },
  methods: {
    ...bind_document_methods,
    on_document_click(event) {
      if (!this.form_dropdown_open) return;
      const root = this.$refs?.formMultiselect;
      if (!root || root.contains(event.target)) return;
      this.form_dropdown_open = false;
    },
    toggle_form_dropdown() {
      if (this.forms_loading || this.creating_form_binding) return;
      this.form_dropdown_open = !this.form_dropdown_open;
    },
    async fetch_available_forms() {
      this.forms_loading = true;
      try {
        const response = await axios.get(GET_FORMS_URL, get_request_config());
        this.available_forms = get_response_items(response.data)
          .map((form) => this.normalize_form(form))
          .filter((form) => form.id && this.is_document_form(form));
      } catch (error) {
        this.available_forms = [];
        this.show_error(error?.message || 'Failed to load document forms.');
      } finally {
        this.forms_loading = false;
      }
    },
    reset_document_form_bindings() {
      this.document_form_bindings = [];
    },
    build_document_form_bindings(payload) {
      return this.extract_form_bindings(this.parse_response_data(payload))
        .map((binding) => this.normalize_form_binding(binding))
        .filter((binding) => this.is_document_form_binding(binding));
    },
    get_form_bindings_request(entity_type_id) {
      return axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({ params: { entity_type_id } })
      );
    },
    async fetch_form_bindings() {
      const entity_type_id = Number(this.entityTypeId || 0);
      if (!entity_type_id) {
        this.reset_document_form_bindings();
        return;
      }
      this.bindings_loading = true;
      const response = await this.get_form_bindings_request(entity_type_id)
        .catch((error) => this.handle_form_bindings_error(error))
        .finally(() => this.clear_form_bindings_loading());
      if (response) this.document_form_bindings = this.build_document_form_bindings(response.data);
    },
    handle_form_bindings_error(error) {
      this.reset_document_form_bindings();
      this.show_error(error?.message || 'Failed to load document form bindings.');
      return null;
    },
    clear_form_bindings_loading() {
      this.bindings_loading = false;
    },
    async load_document_form_workspace() {
      this.loading = true;
      try {
        await this.fetch_available_forms();
        await this.fetch_form_bindings();
      } finally {
        this.loading = false;
      }
    },
    async add_document_form_bindings() {
      const entity_config_id = Number(this.entityConfigId || 0);
      const selected_ids = unique_positive_ids(this.selected_form_ids);
      if (!this.can_create_form_bindings(entity_config_id, selected_ids)) {
        return;
      }
      this.creating_form_binding = true;
      try {
        const results = await this.create_document_form_bindings(entity_config_id, selected_ids);
        await this.fetch_form_bindings();
        await this.fetch_available_forms();
        this.report_binding_results(results);
        this.selected_form_ids = results.failures.map((failure) => String(failure.form_id));
        this.form_dropdown_open = false;
      } finally {
        this.creating_form_binding = false;
      }
    },
    can_create_form_bindings(entity_config_id, selected_ids) {
      if (!entity_config_id) {
        this.show_error('Entity config is not loaded yet. Please wait and try again.');
        return false;
      }
      if (!selected_ids.length) {
        this.show_error('Please select at least one document form to bind.');
        return false;
      }
      return true;
    },
    async create_document_form_bindings(entity_config_id, selected_ids) {
      const results = { successes: [], failures: [] };
      for (const form_id of selected_ids) {
        await this.create_document_form_binding(entity_config_id, form_id, results);
      }
      return results;
    },
    async create_document_form_binding(entity_config_id, form_id, results) {
      const response = await axios.post(
        CREATE_FORM_BINDING_URL,
        { entity_config_id, form_id, priority: 0, unique_question_ids: [] },
        get_request_config()
      ).catch((error) => this.record_binding_failure(results, form_id, error));
      if (!response) return;
      this.add_created_binding(response.data);
      results.successes.push(form_id);
    },
    record_binding_failure(results, form_id, error) {
      results.failures.push(this.build_binding_failure(form_id, error));
      return null;
    },
    add_created_binding(payload) {
      const parsed = this.parse_response_data(payload);
      const binding = parsed?.data ? this.normalize_form_binding(parsed.data) : null;
      if (!binding || !this.is_document_form_binding(binding)) return;
      this.document_form_bindings = [
        ...this.document_form_bindings.filter((item) => Number(item?.id) !== Number(binding?.id)),
        binding
      ];
    },
    build_binding_failure(form_id, error) {
      const parsed = parse_response_payload(error?.response?.data || {});
      return Object.assign({}, {
        form_id,
        message: parsed?.message || error?.message || 'Failed to bind document form.'
      });
    },
    report_binding_results(results) {
      this.report_binding_successes(results.successes);
      this.report_binding_failures(results.failures);
    },
    report_binding_successes(successes) {
      if (!successes.length) return;
      this.show_success(`${successes.length} document form${successes.length === 1 ? '' : 's'} bound successfully.`);
    },
    report_binding_failures(failures) {
      if (!failures.length) return;
      this.show_error(failures.length === 1 ? failures[0].message : `${failures.length} document forms failed to bind.`);
    },
    remove_document_form_binding(binding) {
      this.show_confirm(
        'Remove Document Form Binding?',
        `Are you sure you want to remove ${binding?.form_name || 'this document form'}?`,
        () => this.delete_document_form_binding(binding)
      );
    },
    async delete_document_form_binding(binding) {
      const binding_id = Number(binding?.id || 0);
      if (!binding_id) return;
      this.removing_binding_id = binding_id;
      const response = await axios.post(
        REMOVE_FORM_BINDING_URL,
        { binding_id },
        get_request_config()
      )
        .catch((error) => this.handle_remove_binding_failure(error))
        .finally(() => this.clear_removing_binding(binding_id));
      if (response) await this.sync_removed_form_binding(response.data, binding_id);
    },
    async sync_removed_form_binding(payload, binding_id) {
      const parsed = parse_response_payload(payload);
      this.document_form_bindings = this.document_form_bindings.filter((item) => Number(item?.id) !== binding_id);
      await this.fetch_available_forms();
      this.show_success(parsed?.message || 'Document form binding removed.');
    },
    show_remove_binding_error(error) {
      const parsed = parse_response_payload(error?.response?.data || {});
      this.show_error(parsed?.message || error?.message || 'Failed to remove document form binding.');
    },
    handle_remove_binding_failure(error) {
      this.show_remove_binding_error(error);
      return null;
    },
    clear_removing_binding(binding_id) {
      if (this.removing_binding_id === binding_id) {
        this.removing_binding_id = null;
      }
    },
    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: {
          entityTypeId: this.entityTypeId
        }
      });
    },
  },
};
